import React, { useEffect, useState } from "react";
import { Form, Modal, Select, Typography, message } from "antd";
import {
  addContainerToMaster,
  fetchMasterContainersForFilter,
} from "../../services/masterContainerService";
import type { MasterContainer } from "../../types/booking";
import { getMasterTransportTypeName } from "../../utils/masterContainerTypeUtils";

const { Text } = Typography;

interface AgentAddToMasterModalProps {
  open: boolean;
  agentId: number;
  containerId: number;
  containerReference?: string | null;
  transportTypeId?: number | null;
  transportTypeName?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const AgentAddToMasterModal: React.FC<AgentAddToMasterModalProps> = ({
  open,
  agentId,
  containerId,
  containerReference,
  transportTypeId,
  transportTypeName,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [masters, setMasters] = useState<MasterContainer[]>([]);

  useEffect(() => {
    if (!open || !agentId) return;

    if (transportTypeId == null) {
      setMasters([]);
      return;
    }

    setLoading(true);
    fetchMasterContainersForFilter({
      agentId,
      transportTypeId,
    })
      .then((result) => {
        if (result.error) {
          message.error("Failed to load Master BL/AWB list");
          return;
        }
        setMasters(result.data);
      })
      .finally(() => setLoading(false));
  }, [open, agentId, transportTypeId]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const result = await addContainerToMaster(
        values.master_container_id,
        containerId
      );
      if (!result.success) {
        message.error(
          (result.error as Error)?.message ||
            "Failed to add container to Master BL/AWB"
        );
        return;
      }
      message.success("Container added to Master BL/AWB");
      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Error adding container to master:", error);
      message.error("Failed to add container to Master BL/AWB");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add to Master BL/AWB"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="Add"
      okButtonProps={{
        disabled: transportTypeId == null || masters.length === 0,
      }}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Container: {containerReference || `#${containerId}`}
        {transportTypeName ? ` · ${transportTypeName}` : ""}
      </Text>

      {transportTypeId == null ? (
        <Text type="danger">
          This container has no transport type, so it cannot be linked to a
          Master BL/AWB.
        </Text>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="master_container_id"
            label="Master BL/AWB"
            rules={[{ required: true, message: "Select a Master BL/AWB" }]}
            extra={`Only ${transportTypeName || "matching"} masters for your agent are shown.`}
          >
            <Select
              loading={loading}
              showSearch
              optionFilterProp="label"
              placeholder="Select Master BL/AWB"
              options={masters.map((master) => ({
                value: master.id,
                label: `${master.reference || `Master #${master.id}`} (${getMasterTransportTypeName(master)})`,
              }))}
              notFoundContent={
                loading ? "Loading..." : "No matching Master BL/AWB found"
              }
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
